/**
 * Phase 11 Plan 05 — Testable extract for CommandCenter client row badge +
 * "Migrate to Fanbasis subscription" button (D-14, D-16).
 *
 * Migrate visibility: release.billing_v2 ON AND billing_mode='legacy_cron'
 * AND has_payment_method. Clicking invokes fanbasis-create-subscription-checkout.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Copy, Mail } from 'lucide-react';
import {
  BillingStatusBadge,
  type BillingStatusBadgeProps,
} from '@/components/admin/BillingStatusBadge';

export interface ClientBillingRowProps {
  clientId: string;
  billing_mode: BillingStatusBadgeProps['billing_mode'];
  subscription_status?: BillingStatusBadgeProps['subscription_status'];
  has_payment_method: boolean;
  has_unresolved_dispute?: boolean;
}

export function ClientBillingRow({
  clientId,
  billing_mode,
  subscription_status,
  has_payment_method,
  has_unresolved_dispute,
}: ClientBillingRowProps) {
  const billingV2 = useFeatureFlag('release.billing_v2');
  const [submitting, setSubmitting] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const canMigrate =
    billingV2 && billing_mode === 'legacy_cron' && has_payment_method;

  async function handleMigrate() {
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'fanbasis-create-subscription-checkout',
        { body: { client_id: clientId } },
      );
      if (error || !data?.payment_link) {
        toast.error('Could not create subscription checkout');
        return;
      }
      setPaymentLink(data.payment_link as string);
      setModalOpen(true);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : 'Subscription checkout failed',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCopy() {
    if (!paymentLink) return;
    await navigator.clipboard.writeText(paymentLink);
    toast.success('Payment link copied');
  }

  return (
    <div className="flex items-center gap-2">
      <BillingStatusBadge
        billing_mode={billing_mode}
        subscription_status={subscription_status}
        has_payment_method={has_payment_method}
        has_unresolved_dispute={has_unresolved_dispute}
      />
      {canMigrate && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleMigrate}
          disabled={submitting}
        >
          Migrate to Fanbasis subscription
        </Button>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-surface border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Subscription payment link</DialogTitle>
            <DialogDescription>
              Send this link to the client to complete their subscription
              checkout. Their billing mode will flip to <code>subscription</code>{' '}
              once the webhook fires.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {paymentLink && (
              <code className="block p-2 rounded bg-surface border border-border text-xs break-all">
                {paymentLink}
              </code>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCopy}>
              <Copy className="w-4 h-4 mr-2" />
              Copy link
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (!paymentLink) return;
                window.location.href = `mailto:?subject=${encodeURIComponent(
                  'Venture Hills subscription checkout',
                )}&body=${encodeURIComponent(paymentLink)}`;
              }}
            >
              <Mail className="w-4 h-4 mr-2" />
              Send via email
            </Button>
            <Button onClick={() => setModalOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
